import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { CommandPalette, CommandAction } from './CommandPalette';
import { ActivityStream, ActivityItem } from './ActivityStream';
import { ProposalCard, TradeProposal } from './ProposalCard';
import { IntentResultPanel } from './IntentResultPanel';
import { useOMSWebSocket } from '../../hooks/useOMSWebSocket';
import { agenticApi, collaborationApi, liquidityApi } from '../../services/agenticApi';
import type { AgenticBundle } from '../../types/agentic';
import { AxiomFlowMark } from '../shared/BrandLogo';

// Portfolio type from API
interface Portfolio {
  id: number;
  portfolioCode: string;
  portfolioName: string;
  portfolioType?: string;
  status?: string;
}

// Dashboard Layout - height adjusted to work within OMS navigation (h-16 = 64px)
const DashboardContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 64px);
  background: #0f0f1a;
  color: #ffffff;
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 24px;
  background: #1a1a2e;
  border-bottom: 1px solid #2a2a4a;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const LogoIcon = styled(AxiomFlowMark).attrs({ size: 36 })``;

const LogoText = styled.div`
  display: flex;
  flex-direction: column;
`;

const ProductName = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #ffffff;
`;

const ProductSubtitle = styled.span`
  font-size: 11px;
  color: #6b7280;
`;

const CommandBar = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid #2a2a4a;
  border-radius: 8px;
  color: #9ca3af;
  font-size: 13px;
  cursor: pointer;
  min-width: 280px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: #3b82f6;
  }

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const CommandBarIcon = styled.span`
  font-size: 14px;
`;

const CommandBarText = styled.span`
  flex: 1;
  text-align: left;
`;

const CommandBarShortcut = styled.span`
  padding: 2px 6px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  font-size: 11px;
  font-family: monospace;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const StatusIndicator = styled.div<{ $connected: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  background: ${props => props.$connected ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)'};
  color: ${props => props.$connected ? '#22c55e' : '#ef4444'};
`;

const StatusDot = styled.span<{ $connected: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.$connected ? '#22c55e' : '#ef4444'};
  animation: ${props => props.$connected ? 'pulse 2s ease-in-out infinite' : 'none'};

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;

const RefreshToggle = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.$active ? 'rgba(59, 130, 246, 0.2)' : 'transparent'};
  border: 1px solid ${props => props.$active ? '#3b82f6' : '#2a2a4a'};
  color: ${props => props.$active ? '#3b82f6' : '#9ca3af'};

  &:hover {
    background: rgba(59, 130, 246, 0.1);
    border-color: #3b82f6;
  }
`;

const MainContent = styled.main`
  display: grid;
  grid-template-columns: 1fr 400px 300px;
  gap: 1px;
  flex: 1;
  overflow: hidden;
  background: #2a2a4a;
`;

const Panel = styled.section`
  background: #0f0f1a;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #2a2a4a;
`;

const PanelTitle = styled.h2`
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PanelBadge = styled.span`
  padding: 2px 8px;
  background: rgba(59, 130, 246, 0.2);
  color: #3b82f6;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 500;
`;

const PanelContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
`;

const ProposalsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: #6b7280;
  text-align: center;
`;

const EmptyIcon = styled.span`
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
`;

const EmptyText = styled.p`
  font-size: 14px;
  margin: 0;
`;

const SidePanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  background: #2a2a4a;
`;

const QuickStats = styled.div`
  background: #0f0f1a;
  padding: 16px 20px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

const StatCard = styled.div`
  padding: 12px;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 8px;
  border: 1px solid #2a2a4a;
`;

const StatValue = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: #ffffff;
`;

const StatLabel = styled.div`
  font-size: 11px;
  color: #6b7280;
  margin-top: 4px;
`;

const AgentStatusSection = styled.div`
  background: #0f0f1a;
  padding: 16px 20px;
  flex: 1;
  overflow-y: auto;
`;

const AgentStatusTitle = styled.h3`
  font-size: 12px;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  margin: 0 0 12px;
`;

const AgentStatusList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const AgentStatusCard = styled.div<{ $status: string }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 8px;
  border: 1px solid ${props => {
    switch (props.$status) {
      case 'active': return 'rgba(34, 197, 94, 0.3)';
      case 'processing': return 'rgba(59, 130, 246, 0.3)';
      case 'error': return 'rgba(239, 68, 68, 0.3)';
      default: return '#2a2a4a';
    }
  }};
`;

const AgentIcon = styled.div<{ $type: string }>`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  background: ${props => {
    switch (props.$type) {
      case 'research': return 'rgba(59, 130, 246, 0.2)';
      case 'compliance': return 'rgba(139, 92, 246, 0.2)';
      case 'execution': return 'rgba(34, 197, 94, 0.2)';
      case 'orchestrator': return 'rgba(245, 158, 11, 0.2)';
      default: return 'rgba(107, 114, 128, 0.2)';
    }
  }};
`;

const AgentInfo = styled.div`
  flex: 1;
`;

const AgentName = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #ffffff;
`;

const AgentStatusText = styled.div<{ $status: string }>`
  font-size: 11px;
  color: ${props => {
    switch (props.$status) {
      case 'active': return '#22c55e';
      case 'processing': return '#3b82f6';
      case 'error': return '#ef4444';
      default: return '#6b7280';
    }
  }};
`;

const KeyboardShortcuts = styled.div`
  background: #0f0f1a;
  padding: 16px 20px;
  border-top: 1px solid #2a2a4a;
`;

const ShortcutTitle = styled.h3`
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  margin: 0 0 12px;
`;

const ShortcutList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ShortcutItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
`;

const ShortcutLabel = styled.span`
  color: #9ca3af;
`;

const ShortcutKey = styled.span`
  padding: 2px 6px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid #2a2a4a;
  border-radius: 4px;
  font-family: monospace;
  font-size: 11px;
  color: #e5e7eb;
`;

const QuickActionsPanel = styled.div`
  background: #0f0f1a;
  padding: 16px 20px;
  border-bottom: 1px solid #2a2a4a;
`;

const QuickActionsTitle = styled.h3`
  font-size: 12px;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  margin: 0 0 12px;
`;

const QuickActionsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
`;

const QuickActionButton = styled.button<{ $variant?: 'primary' | 'secondary' | 'success' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 12px;
  background: ${props => {
    switch (props.$variant) {
      case 'primary': return 'rgba(59, 130, 246, 0.15)';
      case 'success': return 'rgba(34, 197, 94, 0.15)';
      default: return 'rgba(255, 255, 255, 0.05)';
    }
  }};
  border: 1px solid ${props => {
    switch (props.$variant) {
      case 'primary': return 'rgba(59, 130, 246, 0.3)';
      case 'success': return 'rgba(34, 197, 94, 0.3)';
      default: return '#2a2a4a';
    }
  }};
  border-radius: 8px;
  color: ${props => {
    switch (props.$variant) {
      case 'primary': return '#60a5fa';
      case 'success': return '#4ade80';
      default: return '#e5e7eb';
    }
  }};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: ${props => {
      switch (props.$variant) {
        case 'primary': return 'rgba(59, 130, 246, 0.25)';
        case 'success': return 'rgba(34, 197, 94, 0.25)';
        default: return 'rgba(255, 255, 255, 0.1)';
      }
    }};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const ButtonIcon = styled.span`
  font-size: 14px;
`;

const PortfolioSelector = styled.select`
  width: 100%;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid #2a2a4a;
  border-radius: 6px;
  color: #e5e7eb;
  font-size: 12px;
  cursor: pointer;
  margin-bottom: 12px;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }

  option {
    background: #1a1a2e;
    color: #e5e7eb;
  }
`;

const PortfolioLabel = styled.label`
  font-size: 11px;
  color: #6b7280;
  margin-bottom: 4px;
  display: block;
`;

interface OMSDashboardProps {
  onCommandExecute?: (command: CommandAction, params?: Record<string, any>) => void;
}

// Agent status type
interface AgentStatus {
  type: 'research' | 'compliance' | 'execution' | 'orchestrator';
  name: string;
  status: 'idle' | 'active' | 'processing' | 'error';
  lastActivity?: string;
}

const mapAgentType = (value?: string): AgentStatus['type'] => {
  const normalized = value?.toLowerCase() || '';

  if (normalized.includes('compliance')) return 'compliance';
  if (normalized.includes('execution') || normalized.includes('liquidity')) return 'execution';
  if (normalized.includes('orchestr')) return 'orchestrator';
  return 'research';
};

const mapExternalStatus = (value?: string): AgentStatus['status'] => {
  const normalized = value?.toLowerCase() || '';

  if (normalized.includes('error') || normalized.includes('fail')) return 'error';
  if (normalized.includes('processing') || normalized.includes('running') || normalized.includes('busy')) return 'processing';
  if (normalized.includes('active')) return 'active';
  return 'idle';
};

export const OMSDashboard: React.FC<OMSDashboardProps> = ({ onCommandExecute }) => {
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);

  // AI Intent state
  const [intentResult, setIntentResult] = useState<AgenticBundle | null>(null);
  const [isExecutingBundle, setIsExecutingBundle] = useState(false);

  // Local activity state (for when WebSocket isn't connected or for simulated flows)
  const [localActivities, setLocalActivities] = useState<ActivityItem[]>([]);
  const [localProposals, setLocalProposals] = useState<TradeProposal[]>([]);
  const [isDiscovering, setIsDiscovering] = useState(false);

  // Portfolio selection for discovery
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [selectedPortfolioId, setSelectedPortfolioId] = useState<string>('');
  const [isLoadingPortfolios, setIsLoadingPortfolios] = useState(true);

  // Fetch portfolios on mount
  useEffect(() => {
    const fetchPortfolios = async () => {
      try {
        setIsLoadingPortfolios(true);
        const response = await axios.get('/api/dimensions/portfolios');
        const data = response.data?.content || response.data || [];
        setPortfolios(data);
        // Select first portfolio by default
        if (data.length > 0) {
          setSelectedPortfolioId(prev => prev || String(data[0].id));
        }
      } catch (error) {
        console.error('Failed to fetch portfolios:', error);
      } finally {
        setIsLoadingPortfolios(false);
      }
    };
    fetchPortfolios();
  }, []);

  const selectedPortfolio = portfolios.find(p => String(p.id) === selectedPortfolioId) || portfolios[0];

  // WebSocket connection
  const {
    isConnected,
    activities: wsActivities,
    proposals: wsProposals,
    clearActivities,
  } = useOMSWebSocket({
    url: 'ws://localhost:8081/ws/agents',
    topics: ['*'],
    autoReconnect: true,
  });

  // Merge WebSocket and local activities/proposals
  const activities = [...localActivities, ...wsActivities].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );
  const proposals = [...localProposals, ...wsProposals];

  // Agent statuses (would come from WebSocket in production)
  const [agentStatuses, setAgentStatuses] = useState<AgentStatus[]>([
    { type: 'research', name: 'Research Agent', status: 'idle', lastActivity: 'Ready' },
    { type: 'compliance', name: 'Compliance Agent', status: 'idle', lastActivity: 'Ready' },
    { type: 'execution', name: 'Execution Agent', status: 'idle', lastActivity: 'Ready' },
    { type: 'orchestrator', name: 'Orchestrator', status: 'idle', lastActivity: 'Ready' },
  ]);

  useEffect(() => {
    let cancelled = false;
    let intervalId: number | undefined;

    const syncCollaborationStatus = async () => {
      const status = await collaborationApi.getStatus();
      if (!status || cancelled) return;

      const nextStatuses = status.agents.map(agent => ({
        type: mapAgentType(agent.agentType || agent.name),
        name: agent.name || agent.agentType || 'Agent',
        status: mapExternalStatus(agent.status),
        lastActivity: agent.currentTask || agent.lastActivity || agent.lastHeartbeat || agent.status,
      })) as AgentStatus[];

      if (nextStatuses.length > 0) {
        setAgentStatuses(nextStatuses);
      }
    };

    syncCollaborationStatus().catch(console.error);

    if (autoRefresh) {
      intervalId = window.setInterval(() => {
        syncCollaborationStatus().catch(console.error);
      }, 20000);
    }

    return () => {
      cancelled = true;
      if (intervalId) window.clearInterval(intervalId);
    };
  }, [autoRefresh]);

  // Helper to add an activity to the local stream
  const addActivity = useCallback((activity: { type: ActivityItem['type']; title: string; description?: string; status: ActivityItem['status']; metadata?: Record<string, any> }) => {
    const newActivity: ActivityItem = {
      id: `local-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: activity.type,
      title: activity.title,
      description: activity.description,
      status: activity.status,
      timestamp: new Date(),
      metadata: activity.metadata,
    };
    setLocalActivities(prev => [newActivity, ...prev].slice(0, 50));
    return newActivity;
  }, []);

  // Helper to update an existing activity
  const updateActivity = useCallback((id: string, updates: { title?: string; description?: string; status?: ActivityItem['status']; metadata?: Record<string, any> }) => {
    setLocalActivities(prev =>
      prev.map(a => (a.id === id ? { ...a, ...updates } : a))
    );
  }, []);

  // Helper to update agent status
  const updateAgentStatus = useCallback((type: AgentStatus['type'], status: AgentStatus['status'], lastActivity: string) => {
    setAgentStatuses(prev =>
      prev.map(a => (a.type === type ? { ...a, status, lastActivity } : a))
    );
  }, []);

  // Quick stats
  const pendingProposals = proposals.filter(p => p.status === 'pending').length;
  const executedToday = activities.filter(a =>
    a.type === 'trade_executed' &&
    new Date(a.timestamp).toDateString() === new Date().toDateString()
  ).length;

  // Handle keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowCommandPalette(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleDiscoverOpportunities = useCallback(async () => {
    if (isDiscovering || !selectedPortfolio) return;

    setIsDiscovering(true);
    updateAgentStatus('orchestrator', 'processing', 'Coordinating discovery...');
    const orchestratorActivity = addActivity({
      type: 'system_event',
      title: 'Orchestrator: Discovery Started',
      description: `Initiating discovery for ${selectedPortfolio.portfolioName}`,
      status: 'in_progress',
      metadata: { action: 'discovery_start', portfolioId: selectedPortfolio.id },
    });

    try {
      const discoveryResult = await collaborationApi.discover();
      const posResponse = await axios.get(`/api/positions/portfolio/${selectedPortfolio.id}`);
      const positions = posResponse.data?.content || posResponse.data || [];

      if (!Array.isArray(positions) || positions.length === 0) {
        updateActivity(orchestratorActivity.id, {
          status: 'warning',
          title: 'Discovery Started Without Portfolio Positions',
          description: `${selectedPortfolio.portfolioName} has no positions available for a local review. Discovery was started at the collaboration service level.`,
          metadata: { evaluationsStarted: discoveryResult.evaluationsStarted },
        });
      } else {
        updateActivity(orchestratorActivity.id, {
          status: 'success',
          title: 'Discovery In Progress',
          description: `Started ${discoveryResult.evaluationsStarted} collaborative evaluations across ${positions.length} positions in ${selectedPortfolio.portfolioName}. Watch the activity stream and proposals panel for incoming results.`,
          metadata: {
            portfolioId: selectedPortfolio.id,
            positionCount: positions.length,
            evaluationsStarted: discoveryResult.evaluationsStarted,
          },
        });
      }

      addActivity({
        type: 'system_event',
        title: 'Discovery Request Accepted',
        description: `Collaboration pipeline accepted the request at ${new Date(discoveryResult.timestamp).toLocaleTimeString()}.`,
        status: 'success',
        metadata: { evaluationsStarted: discoveryResult.evaluationsStarted },
      });
    } catch (error: any) {
      console.error('Discovery failed:', error);
      updateActivity(orchestratorActivity.id, {
        status: 'error',
        title: 'Discovery Failed',
        description: error.message || 'Failed to start discovery.',
      });
      updateAgentStatus('orchestrator', 'error', 'Discovery failed');
      setIsDiscovering(false);
      return;
    }

    updateAgentStatus('orchestrator', 'idle', 'Discovery queued');
    setIsDiscovering(false);
  }, [isDiscovering, addActivity, updateActivity, updateAgentStatus, selectedPortfolio]);

  // Handle compliance check
  const handleRunCompliance = useCallback(async () => {
    updateAgentStatus('compliance', 'processing', 'Running checks...');
    const activity = addActivity({
      type: 'compliance_check',
      title: 'Compliance Agent: Running Checks',
      description: 'Running comprehensive compliance validation',
      status: 'in_progress',
      metadata: {},
    });

    try {
      const result = await collaborationApi.runCompliance();
      const unavailable = result.status === 'unavailable';

      updateActivity(activity.id, {
        status: unavailable ? 'warning' : 'success',
        title: unavailable ? 'Compliance Endpoint Unavailable' : 'Compliance Request Submitted',
        description: result.message,
        metadata: { status: result.status },
      });
      updateAgentStatus('compliance', 'idle', unavailable ? 'Endpoint unavailable' : 'Request submitted');
    } catch (error: any) {
      updateActivity(activity.id, {
        status: 'error',
        title: 'Compliance Request Failed',
        description: error.message || 'Failed to start compliance checks.',
      });
      updateAgentStatus('compliance', 'error', 'Request failed');
    }
  }, [addActivity, updateActivity, updateAgentStatus]);

  // Handle liquidity scan
  const handleScanLiquidity = useCallback(async () => {
    if (!selectedPortfolio) {
      addActivity({
        type: 'system_event',
        title: 'No Portfolio Selected',
        description: 'Please select a portfolio to scan liquidity',
        status: 'warning',
        metadata: {},
      });
      return;
    }

    updateAgentStatus('execution', 'active', 'Scanning liquidity...');
    const activity = addActivity({
      type: 'trade_sent',
      title: 'Execution Agent: Liquidity Scan',
      description: `Scanning liquidity for ${selectedPortfolio.portfolioName}`,
      status: 'in_progress',
      metadata: { portfolioId: selectedPortfolio.id },
    });

    try {
      // Fetch positions from the selected portfolio
      const posResponse = await axios.get(`/api/positions/portfolio/${selectedPortfolio.id}`);
      const positions = posResponse.data?.content || posResponse.data || [];

      if (positions.length === 0) {
        updateActivity(activity.id, {
          status: 'warning',
          title: 'No Positions Found',
          description: `No positions in ${selectedPortfolio.portfolioName} to scan`,
        });
        updateAgentStatus('execution', 'idle', 'No positions');
        return;
      }

      // Get unique CUSIPs
      const cusips = positions
        .map((p: any) => p.cusip)
        .filter((c: string) => c && c.length > 0);

      if (cusips.length === 0) {
        updateActivity(activity.id, {
          status: 'warning',
          title: 'No CUSIPs Found',
          description: 'Positions do not have valid CUSIPs',
        });
        updateAgentStatus('execution', 'idle', 'No CUSIPs');
        return;
      }

      updateActivity(activity.id, {
        description: `Scanning ${cusips.length} securities via TRACE...`,
      });

      const portfolioScan = await liquidityApi.scanPortfolio(cusips);

      // Update main activity with summary
      updateActivity(activity.id, {
        status: portfolioScan.securityScans.length > 0 ? 'success' : 'warning',
        title: portfolioScan.securityScans.length > 0 ? 'Execution Agent: Liquidity Scan Complete' : 'Execution Agent: No Liquidity Data Returned',
        description: portfolioScan.securityScans.length > 0
          ? `Scanned ${portfolioScan.totalSecurities} securities. Avg score: ${portfolioScan.avgLiquidityScore.toFixed(1)}/10`
          : `Scanned ${portfolioScan.totalSecurities} securities but no venue liquidity data was returned.`,
        metadata: {
          totalSecurities: portfolioScan.totalSecurities,
          liquidCount: portfolioScan.liquidCount,
          illiquidCount: portfolioScan.illiquidCount,
          avgScore: portfolioScan.avgLiquidityScore,
        },
      });

      // Add detailed results for each security
      for (const scan of portfolioScan.securityScans.slice(0, 5)) {
        const scoreColor = scan.liquidityScore >= 7 ? 'success' :
                          scan.liquidityScore >= 4 ? 'in_progress' : 'warning';

        addActivity({
          type: 'system_event',
          title: `${scan.cusip}: ${scan.classification} Liquidity`,
          description: `Score: ${scan.liquidityScore.toFixed(1)}/10. ${scan.message}${scan.mostActiveDealer ? ` Most active: ${scan.mostActiveDealer}` : ''}`,
          status: scoreColor as 'success' | 'in_progress' | 'warning',
          metadata: {
            cusip: scan.cusip,
            score: scan.liquidityScore,
            classification: scan.classification,
            tradeCount: scan.tradeCount,
            mostActiveDealer: scan.mostActiveDealer,
          },
        });
      }

      // If there are illiquid securities, warn about them
      if (portfolioScan.illiquidCount > 0) {
        addActivity({
          type: 'compliance_check',
          title: `${portfolioScan.illiquidCount} Illiquid Securities`,
          description: `Consider widening dealer list for: ${portfolioScan.illiquidSecurities.slice(0, 3).join(', ')}${portfolioScan.illiquidSecurities.length > 3 ? '...' : ''}`,
          status: 'warning',
          metadata: { illiquidCusips: portfolioScan.illiquidSecurities },
        });
      }

      updateAgentStatus(
        'execution',
        'idle',
        portfolioScan.securityScans.length > 0
          ? `Scan complete (${portfolioScan.avgLiquidityScore.toFixed(1)}/10 avg)`
          : 'No liquidity data returned',
      );

    } catch (error: any) {
      console.error('Liquidity scan failed:', error);
      updateActivity(activity.id, {
        status: 'error',
        title: 'Liquidity Scan Failed',
        description: error.message || 'Failed to scan liquidity. Check console for details.',
      });
      updateAgentStatus('execution', 'idle', 'Scan failed');
    }
  }, [selectedPortfolio, addActivity, updateActivity, updateAgentStatus]);

  // Handle command execution
  const handleCommandExecute = useCallback(async (action: CommandAction, params?: Record<string, any>) => {
    console.log('Executing command:', action.id, params);

    // Handle specific commands
    if (action.id === 'discover' || action.id === 'discover-opportunities') {
      handleDiscoverOpportunities();
      return;
    }
    if (action.id === 'compliance' || action.id === 'run-compliance') {
      handleRunCompliance();
      return;
    }
    if (action.id === 'liquidity' || action.id === 'scan-liquidity') {
      handleScanLiquidity();
      return;
    }

    onCommandExecute?.(action, params);
  }, [onCommandExecute, handleDiscoverOpportunities, handleRunCompliance, handleScanLiquidity]);

  // Helper to update a proposal's status
  const updateProposal = useCallback((proposalId: string, updates: Partial<TradeProposal>) => {
    setLocalProposals(prev =>
      prev.map(p => (p.id === proposalId ? { ...p, ...updates } : p))
    );
  }, []);

  // Handle proposal actions
  const handleApprove = useCallback((proposalId: string) => {
    console.log('Approving proposal:', proposalId);
    updateProposal(proposalId, { status: 'approved', workflowStage: 'APPROVED' });
    addActivity({
      type: 'trade_approved',
      title: 'Proposal Approved',
      description: `Trade proposal ${proposalId} approved and ready to open in a trade ticket`,
      status: 'success',
      metadata: { proposalId },
    });
  }, [updateProposal, addActivity]);

  const handleReject = useCallback((proposalId: string, reason: string) => {
    console.log('Rejecting proposal:', proposalId, reason);
    updateProposal(proposalId, { status: 'rejected' });
    addActivity({
      type: 'trade_rejected',
      title: 'Proposal Rejected',
      description: `Trade proposal ${proposalId} rejected: ${reason || 'No reason provided'}`,
      status: 'warning',
      metadata: { proposalId, reason },
    });
  }, [updateProposal, addActivity]);

  const handleExecute = useCallback(async (proposalId: string) => {
    console.log('Executing proposal:', proposalId);

    // Find the proposal
    const proposal = localProposals.find(p => p.id === proposalId);
    if (!proposal) {
      console.error('Proposal not found:', proposalId);
      return;
    }

    const activity = addActivity({
      type: 'trade_created',
      title: 'Preparing Trade Ticket',
      description: `Loading ticket context for ${proposal.trade.security}`,
      status: 'in_progress',
      metadata: { proposalId },
    });

    try {
      const cusip = proposal.trade.cusip || '';
      const securityName = proposal.trade.security?.toLowerCase() || '';

      // Check if this is a cash position (not a tradeable security)
      const isCashPosition = cusip.includes('CASH') ||
                            securityName.includes('cash') ||
                            cusip.match(/^[A-Z]{3}_CASH$/i); // Pattern like USD_CASH, EUR_CASH

      if (isCashPosition) {
        // Cash positions go to the Cash Transfer ticket
        const tradeData = {
          cusip: proposal.trade.cusip,
          securityName: proposal.trade.security,
          portfolioId: proposal.trade.portfolioId,
          portfolioName: proposal.trade.portfolioName,
          transactionType: proposal.trade.side === 'BUY' ? 'DEPOSIT' : 'WITHDRAWAL',
          amount: proposal.trade.notional || proposal.trade.quantity,
          currency: cusip.replace('_CASH', '') || 'USD',
          assetClassName: 'Cash',
          notes: `Created from AI proposal: ${proposal.rationale.summary}`,
          proposalId: proposalId,
        };

        sessionStorage.setItem('pendingTradeData', JSON.stringify(tradeData));

        updateActivity(activity.id, {
          status: 'success',
          title: 'Opening Cash Transfer',
          description: 'Cash transfer ticket is ready for completion.',
        });

        addActivity({
          type: 'trade_created',
          title: 'Cash Transfer Ticket Ready',
          description: 'Opening the cash transfer form with proposal details prefilled.',
          status: 'success',
          metadata: { route: '/trade-entry/cash', proposalId },
        });

        window.location.href = '/trade-entry/cash';
        return;
      }

      // Look up security by CUSIP to get asset class
      const securityResponse = await axios.get(`/api/dimensions/securities/cusip/${proposal.trade.cusip}`);
      const security = securityResponse.data;

      if (!security?.id) {
        throw new Error(`Security with CUSIP ${proposal.trade.cusip} not found`);
      }

      // Determine the trade entry route based on asset class
      const assetClassName = security.assetClassName?.toLowerCase() || '';
      const assetSubClassName = security.assetSubClassName?.toLowerCase() || '';

      let tradeEntryRoute = '/trade-entry/fixed-income'; // Default to fixed income

      if (assetClassName.includes('real estate') || assetSubClassName.includes('cml')) {
        tradeEntryRoute = '/trade-entry/real-estate';
      } else if (assetSubClassName.includes('ir swap') || assetSubClassName.includes('interest rate swap')) {
        tradeEntryRoute = '/trade-entry/derivatives/ir-swap';
      } else if (assetSubClassName.includes('cd swap') || assetSubClassName.includes('credit default swap')) {
        tradeEntryRoute = '/trade-entry/derivatives/cd-swap';
      } else if (assetSubClassName.includes('future')) {
        tradeEntryRoute = '/trade-entry/derivatives/futures';
      } else if (assetSubClassName.includes('option')) {
        tradeEntryRoute = '/trade-entry/derivatives/options';
      } else if (assetClassName.includes('derivative') || assetSubClassName.includes('swap')) {
        tradeEntryRoute = '/trade-entry/derivatives/ir-swap'; // Default to IR swap for derivatives
      } else if (
        assetClassName.includes('leveraged') ||
        assetSubClassName.includes('leveraged') ||
        assetSubClassName.includes('term loan') ||
        assetSubClassName.includes('syndicated') ||
        assetSubClassName.includes('revolver')
      ) {
        tradeEntryRoute = '/trade-entry/leveraged-loan';
      } else if (assetClassName.includes('private') || assetSubClassName.includes('private')) {
        tradeEntryRoute = '/trade-entry/private-fixed-income';
      } else if (assetClassName.includes('fx') || assetClassName.includes('foreign exchange')) {
        tradeEntryRoute = '/trade-entry/fx';
      } else if (assetClassName.includes('repo')) {
        tradeEntryRoute = '/trade-entry/repo';
      }

      // Store proposal data in sessionStorage for the trade entry form to pick up
      const tradeData = {
        securityId: security.id,
        cusip: proposal.trade.cusip,
        securityName: proposal.trade.security,
        portfolioId: proposal.trade.portfolioId,
        portfolioName: proposal.trade.portfolioName,
        transactionType: proposal.trade.side,
        quantity: proposal.trade.quantity,
        amount: proposal.trade.notional || proposal.trade.quantity,
        price: proposal.trade.price || 100,
        assetClassName: security.assetClassName,
        assetSubClassName: security.assetSubClassName,
        notes: `Created from AI proposal: ${proposal.rationale.summary}`,
        proposalId: proposalId,
      };

      sessionStorage.setItem('pendingTradeData', JSON.stringify(tradeData));

      updateActivity(activity.id, {
        status: 'success',
        title: 'Trade Ticket Ready',
        description: `Opening ${security.assetClassName || 'Fixed Income'} trade entry with proposal details.`,
      });

      // Add activity with navigation info
      addActivity({
        type: 'trade_created',
        title: 'Complete Trade Ticket',
        description: `Review the ${security.assetClassName || 'Fixed Income'} ticket, complete the required fields, and submit the order.`,
        status: 'success',
        metadata: { route: tradeEntryRoute, proposalId },
      });

      // Navigate to the appropriate trade entry form
      window.location.href = tradeEntryRoute;

    } catch (error: any) {
      console.error('Failed to create trade:', error);
      updateActivity(activity.id, {
        status: 'error',
        title: 'Trade Creation Failed',
        description: error.response?.data?.message || error.message || 'Failed to create trade',
      });
    }
  }, [localProposals, addActivity, updateActivity]);

  // Handle AI intent result from CommandPalette
  const handleIntentResult = useCallback((bundle: AgenticBundle) => {
    console.log('Received AI intent result:', bundle);
    setIntentResult(bundle);
  }, []);

  // Handle bundle execution
  const handleExecuteBundle = useCallback(async (bundleId: string, simulateOnly = false) => {
    setIsExecutingBundle(true);
    try {
      const result = await agenticApi.executeBundle(bundleId, { simulateOnly });
      console.log('Bundle execution result:', result);

      if (result.status === 'COMPLETED' || result.status === 'SIMULATED') {
        addActivity({
          type: simulateOnly ? 'system_event' : 'trade_created',
          title: simulateOnly ? 'Bundle Simulation Completed' : 'Bundle Execution Submitted',
          description: simulateOnly
            ? result.message
            : `${result.message}${result.orderIds.length > 0 ? ` Order IDs: ${result.orderIds.join(', ')}` : ''}`,
          status: 'success',
          metadata: { bundleId, orderIds: result.orderIds, status: result.status },
        });
        setIntentResult(null);
      } else {
        addActivity({
          type: 'trade_failed',
          title: 'Bundle Execution Failed',
          description: result.message,
          status: 'error',
          metadata: { bundleId, status: result.status },
        });
      }
    } catch (error: any) {
      console.error('Bundle execution failed:', error);
      addActivity({
        type: 'trade_failed',
        title: 'Bundle Execution Failed',
        description: error.message || 'Failed to execute bundle. Please try again.',
        status: 'error',
        metadata: { bundleId },
      });
    } finally {
      setIsExecutingBundle(false);
    }
  }, [addActivity]);

  const getAgentIcon = (type: string): string => {
    switch (type) {
      case 'research': return '🔬';
      case 'compliance': return '⚖️';
      case 'execution': return '⚡';
      case 'orchestrator': return '🎯';
      default: return '🤖';
    }
  };

  return (
    <DashboardContainer>
      <Header>
        <Logo>
          <LogoIcon />
          <LogoText>
            <ProductName>Axiom OMS</ProductName>
            <ProductSubtitle>Order Management System</ProductSubtitle>
          </LogoText>
        </Logo>

        <CommandBar onClick={() => setShowCommandPalette(true)}>
          <CommandBarIcon>🔍</CommandBarIcon>
          <CommandBarText>Search commands, trades, portfolios...</CommandBarText>
          <CommandBarShortcut>⌘K</CommandBarShortcut>
        </CommandBar>

        <HeaderActions>
          <StatusIndicator $connected={isConnected}>
            <StatusDot $connected={isConnected} />
            {isConnected ? 'Connected' : 'Disconnected'}
          </StatusIndicator>

          <RefreshToggle
            $active={autoRefresh}
            onClick={() => setAutoRefresh(!autoRefresh)}
          >
            🔄 Auto-refresh {autoRefresh ? 'ON' : 'OFF'}
          </RefreshToggle>
        </HeaderActions>
      </Header>

      <MainContent>
        {/* Activity Stream Panel */}
        <Panel>
          <PanelHeader>
            <PanelTitle>
              Activity Stream
              {activities.length > 0 && (
                <PanelBadge>{activities.length}</PanelBadge>
              )}
            </PanelTitle>
            <button
              onClick={() => {
                clearActivities();
                setLocalActivities([]);
              }}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#6b7280',
                fontSize: '12px',
                cursor: 'pointer',
              }}
            >
              Clear
            </button>
          </PanelHeader>
          <PanelContent>
            <ActivityStream
              activities={activities}
              maxHeight="500px"
              showFilters={true}
            />
          </PanelContent>
        </Panel>

        {/* Proposals Panel */}
        <Panel>
          <PanelHeader>
            <PanelTitle>
              Trade Proposals
              {pendingProposals > 0 && (
                <PanelBadge>{pendingProposals} pending</PanelBadge>
              )}
            </PanelTitle>
          </PanelHeader>
          <PanelContent>
            {proposals.length > 0 ? (
              <ProposalsList>
                {proposals.map((proposal) => (
                  <ProposalCard
                    key={proposal.id}
                    proposal={proposal}
                    onApprove={handleApprove}
                    onReject={handleReject}
                    onExecute={handleExecute}
                  />
                ))}
              </ProposalsList>
            ) : (
              <EmptyState>
                <EmptyIcon>📋</EmptyIcon>
                <EmptyText>No pending proposals</EmptyText>
              </EmptyState>
            )}
          </PanelContent>
        </Panel>

        {/* Side Panel */}
        <SidePanel>
          <QuickStats>
            <StatCard>
              <StatValue>{pendingProposals}</StatValue>
              <StatLabel>Pending Review</StatLabel>
            </StatCard>
            <StatCard>
              <StatValue>{executedToday}</StatValue>
              <StatLabel>Executed Today</StatLabel>
            </StatCard>
          </QuickStats>

          <QuickActionsPanel>
            <QuickActionsTitle>Quick Actions</QuickActionsTitle>
            <div>
              <PortfolioLabel>Target Portfolio</PortfolioLabel>
              <PortfolioSelector
                value={selectedPortfolioId}
                onChange={(e) => setSelectedPortfolioId(e.target.value)}
                disabled={isLoadingPortfolios}
              >
                {isLoadingPortfolios ? (
                  <option>Loading portfolios...</option>
                ) : portfolios.length === 0 ? (
                  <option>No portfolios available</option>
                ) : (
                  portfolios.map(p => (
                    <option key={p.id} value={String(p.id)}>{p.portfolioName}</option>
                  ))
                )}
              </PortfolioSelector>
            </div>
            <QuickActionsGrid>
              <QuickActionButton
                $variant="primary"
                onClick={handleDiscoverOpportunities}
                disabled={isDiscovering}
              >
                <ButtonIcon>{isDiscovering ? '⏳' : '🔍'}</ButtonIcon>
                {isDiscovering ? 'Discovering...' : 'Discover'}
              </QuickActionButton>
              <QuickActionButton
                $variant="secondary"
                onClick={handleRunCompliance}
              >
                <ButtonIcon>⚖️</ButtonIcon>
                Compliance
              </QuickActionButton>
              <QuickActionButton
                $variant="success"
                onClick={handleScanLiquidity}
              >
                <ButtonIcon>📊</ButtonIcon>
                Liquidity
              </QuickActionButton>
              <QuickActionButton
                onClick={() => setShowCommandPalette(true)}
              >
                <ButtonIcon>⌨️</ButtonIcon>
                Commands
              </QuickActionButton>
            </QuickActionsGrid>
          </QuickActionsPanel>

          <AgentStatusSection>
            <AgentStatusTitle>Agent Status</AgentStatusTitle>
            <AgentStatusList>
              {agentStatuses.map((agent) => (
                <AgentStatusCard key={agent.type} $status={agent.status}>
                  <AgentIcon $type={agent.type}>
                    {getAgentIcon(agent.type)}
                  </AgentIcon>
                  <AgentInfo>
                    <AgentName>{agent.name}</AgentName>
                    <AgentStatusText $status={agent.status}>
                      {agent.lastActivity || agent.status}
                    </AgentStatusText>
                  </AgentInfo>
                </AgentStatusCard>
              ))}
            </AgentStatusList>
          </AgentStatusSection>

          <KeyboardShortcuts>
            <ShortcutTitle>Shortcuts</ShortcutTitle>
            <ShortcutList>
              <ShortcutItem>
                <ShortcutLabel>Command Palette</ShortcutLabel>
                <ShortcutKey>⌘K</ShortcutKey>
              </ShortcutItem>
              <ShortcutItem>
                <ShortcutLabel>New Trade</ShortcutLabel>
                <ShortcutKey>⌘N</ShortcutKey>
              </ShortcutItem>
              <ShortcutItem>
                <ShortcutLabel>Search</ShortcutLabel>
                <ShortcutKey>⌘F</ShortcutKey>
              </ShortcutItem>
              <ShortcutItem>
                <ShortcutLabel>Refresh</ShortcutLabel>
                <ShortcutKey>⌘R</ShortcutKey>
              </ShortcutItem>
            </ShortcutList>
          </KeyboardShortcuts>
        </SidePanel>
      </MainContent>

      {/* Command Palette */}
      <CommandPalette
        isOpen={showCommandPalette}
        onClose={() => setShowCommandPalette(false)}
        onExecute={handleCommandExecute}
        onIntentResult={handleIntentResult}
        portfolios={portfolios.map(portfolio => ({
          id: portfolio.id,
          name: portfolio.portfolioName,
        }))}
      />

      {/* AI Intent Result Panel */}
      <AnimatePresence>
        {intentResult && (
          <IntentResultPanel
            bundle={intentResult}
            onExecute={handleExecuteBundle}
            onDismiss={() => setIntentResult(null)}
            isExecuting={isExecutingBundle}
          />
        )}
      </AnimatePresence>
    </DashboardContainer>
  );
};

export default OMSDashboard;
